import { ContactEmailModel } from "../models/CustomerModels";
import { dedupeCaseInsensitive } from "./dedupeCaseInsensitive";

export type DraftRecipientMode = "primary" | "all";

export const DEFAULT_DRAFT_RECIPIENT_MODE: DraftRecipientMode = "primary";

export type DraftRecipients = {
  to: string[];
  cc: string[];
};

function normalizeEmail(email: string | undefined | null): string | undefined {
  const trimmed = email?.trim();
  return trimmed ? trimmed : undefined;
}

function getContactEmail(contact: ContactEmailModel): string | undefined {
  return normalizeEmail(contact.email) ?? normalizeEmail(contact.emails?.[0]);
}

export function hasMultipleContactEmails(contacts: ContactEmailModel[]): boolean {
  const emails = contacts.map(getContactEmail).filter(Boolean);
  return new Set(emails).size > 1;
}

function getPrimaryContactEmails(contacts: ContactEmailModel[]): string[] {
  if (!contacts.length) return [];

  const primaryEmails = contacts
    .filter((contact) => contact.isPrimary)
    .map(getContactEmail)
    .filter((email): email is string => !!email);

  if (primaryEmails.length) return dedupeCaseInsensitive(primaryEmails);

  const fallback = getContactEmail(contacts[0]);
  return fallback ? [fallback] : [];
}

export function getOtherContactEmails(contacts: ContactEmailModel[]): string[] {
  const primaryLower = new Set(getPrimaryContactEmails(contacts).map((email) => email.toLowerCase()));
  if (!primaryLower.size) return [];

  return [
    ...new Set(
      contacts
        .map(getContactEmail)
        .filter((email): email is string => !!email && !primaryLower.has(email.toLowerCase()))
    ),
  ];
}

export function hasOtherContacts(contacts: ContactEmailModel[]): boolean {
  return getOtherContactEmails(contacts).length > 0;
}

export function resolveDraftRecipients(
  emailTo: string[],
  contacts: ContactEmailModel[] | undefined,
  mode: DraftRecipientMode,
  emailCC: string[] = []
): DraftRecipients {
  const backendEmails = emailTo.map(normalizeEmail).filter((email): email is string => !!email);
  const backendCcEmails = emailCC.map(normalizeEmail).filter((email): email is string => !!email);
  const contactEmails = contacts?.map(getContactEmail).filter((email): email is string => !!email) ?? [];
  const contactEmailsLower = new Set(contactEmails.map((email) => email.toLowerCase()));

  const primaryEmails = contacts?.length ? getPrimaryContactEmails(contacts) : [];
  const toPrimaryEmails = primaryEmails.length > 0 ? primaryEmails : backendEmails[0] ? [backendEmails[0]] : [];

  // The recipient mode only governs contact emails; backend To addresses that
  // aren't contact emails are explicit recipients and always stay in To.
  const to = dedupeCaseInsensitive([
    ...toPrimaryEmails,
    ...backendEmails.filter((email) => !contactEmailsLower.has(email.toLowerCase())),
  ]);
  const toLower = new Set(to.map((email) => email.toLowerCase()));

  // Backend-provided CC is always honoured; "all" mode additionally CCs the other contacts.
  const ccEmails = mode === "primary" ? backendCcEmails : [...backendCcEmails, ...backendEmails, ...contactEmails];
  const cc = dedupeCaseInsensitive(ccEmails).filter((email) => !toLower.has(email.toLowerCase()));

  return { to, cc };
}
